import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserPlus, LogIn, ChevronDown } from 'lucide-react';
import { useAuth } from '@shared/context/AuthContext';
import { useLanguage } from '@shared/context/LanguageContext';
import { theme } from '@shared/styles/theme';
import { styles } from './RegisterForm.styles';
import { PasswordFields } from './PasswordFields';
import { getGradeLevels } from './registerFormHelpers';

export interface RegisterFormProps {
  onSuccess?: () => void;
}

/**
 * RegisterForm component - Minecraft themed registration form
 */
export const RegisterForm: React.FC<RegisterFormProps> = ({ onSuccess }) => {
  const { t } = useTranslation('auth');
  const { language, setLanguage, availableLanguages } = useLanguage();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [gradeLevel, setGradeLevel] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { isLoading, error, fieldErrors, register, clearError } = useAuth();

  // eslint-disable-next-line sonarjs/slow-regex
  const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const isPasswordValid = (): boolean => {
    return password.length >= 8 && /[A-Z]/.test(password) && /[a-z]/.test(password) && /\d/.test(password) && /[!@#$%^&*]/.test(password);
  };

  const allRequirementsMet = isPasswordValid();
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (!name || !email || !gradeLevel || !password || !confirmPassword) {
      return;
    }

    if (!allRequirementsMet || !passwordsMatch || !isValidEmail(email)) {
      return;
    }

    try {
      await register(name, email, password);
      onSuccess?.();
    } catch {
      // Error is handled by the hook and displayed via error state
    }
  };

  const isFormValid =
    name &&
    email &&
    isValidEmail(email) &&
    gradeLevel &&
    password &&
    confirmPassword &&
    allRequirementsMet &&
    passwordsMatch;

  return (
    <form onSubmit={handleSubmit} style={styles.container}>
      <h2 style={styles.title}>{t('register.title')}</h2>

      {/* Language Selection */}
      <div style={styles.languageSection}>
        <label style={styles.languageLabel}>{t('languageSelector.chooseLanguage')}</label>
        <div style={styles.languageButtons}>
          {availableLanguages.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => setLanguage(lang.code)}
              style={{
                ...styles.languageButton,
                ...(language === lang.code ? styles.languageButtonActive : {}),
              }}
            >
              <span style={styles.languageFlag}>{lang.flag}</span>
              <span>{lang.name}</span>
            </button>
          ))}
        </div>
      </div>

      {error && <div style={styles.errorMessage}>{error}</div>}

      <div style={styles.formGroup}>
        <label htmlFor="name" style={styles.label}>
          {t('register.nameLabel')}
        </label>
        <input
          id="name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('register.namePlaceholder')}
          disabled={isLoading}
          required
          style={styles.input}
          className="minecraft-input"
        />
        {fieldErrors?.Name && <p style={styles.errorText}>{fieldErrors.Name[0]}</p>}
      </div>

      <div style={styles.formGroup}>
        <label htmlFor="email" style={styles.label}>
          {t('register.emailLabel')}
        </label>
        <input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('register.emailPlaceholder')}
          disabled={isLoading}
          required
          style={styles.input}
          className="minecraft-input"
        />
        {email && !isValidEmail(email) && (
          <p style={styles.errorText}>{t('errors.invalidEmail')}</p>
        )}
        {fieldErrors?.Email && <p style={styles.errorText}>{fieldErrors.Email[0]}</p>}
      </div>

      <div style={styles.formGroup}>
        <label htmlFor="gradeLevel" style={styles.label}>
          {t('register.gradeLevelLabel')}
        </label>
        <div style={styles.selectWrapper}>
          <select
            id="gradeLevel"
            value={gradeLevel}
            onChange={(e) => setGradeLevel(e.target.value)}
            disabled={isLoading}
            required
            style={styles.select}
          >
            {getGradeLevels(t).map((grade) => (
              <option key={grade.value} value={grade.value} disabled={grade.value === ''}>
                {grade.label}
              </option>
            ))}
          </select>
          <span style={styles.selectIcon}>
            <ChevronDown size={16} color={theme.colors.text.secondary} aria-hidden="true" />
          </span>
        </div>
      </div>

      <PasswordFields
        password={password}
        confirmPassword={confirmPassword}
        showPassword={showPassword}
        showConfirmPassword={showConfirmPassword}
        isLoading={isLoading}
        onPasswordChange={setPassword}
        onConfirmPasswordChange={setConfirmPassword}
        onToggleShowPassword={() => setShowPassword(!showPassword)}
        onToggleShowConfirmPassword={() => setShowConfirmPassword(!showConfirmPassword)}
      />

      <button
        type="submit"
        disabled={isLoading || !isFormValid}
        style={{
          ...styles.primaryButton,
          opacity: isLoading || !isFormValid ? 0.6 : 1,
        }}
        className="minecraft-button"
      >
        <UserPlus size={20} aria-hidden="true" />
        <span>{isLoading ? t('register.registering') : t('register.submitButton')}</span>
      </button>

      <div style={styles.divider}>
        <span style={styles.dividerLine} />
        <span style={styles.dividerText}>{t('register.haveAccount')}</span>
        <span style={styles.dividerLine} />
      </div>

      <Link to="/login" style={styles.secondaryButton} className="minecraft-button">
        <LogIn size={20} aria-hidden="true" />
        <span>{t('register.loginLink')}</span>
      </Link>
    </form>
  );
};
